import assign from 'lodash.assign';

const { createHigherOrderComponent } = wp.compose;
const { Fragment } = wp.element;
const { InspectorControls } = wp.blockEditor;;
const { PanelBody, PanelRow, TextareaControl, TextControl, FormToggle } = wp.components;
const { addFilter } = wp.hooks;
const { __ } = wp.i18n;

// Enable data attributes control on the following blocks
const enableDataPinOnBlocks = [
	'core/image',
];

/**
 * Add data-pin-xxx HTML5 fields to block.
 *
 * @param {object} settings Current block settings.
 * @param {string} name Name of block.
 *
 * @returns {object} Modified block settings.
 */
const addDataPinAttributes = ( settings, name ) => {
	// Do nothing if it's another block than our defined ones.
	if ( ! enableDataPinOnBlocks.includes( name ) ) {
		return settings;
	}

	// Use Lodash's assign to gracefully handle if attributes are undefined
	settings.attributes = assign( settings.attributes, {
		pin_description: {
			type: 'string',
			default: null,
      selector: 'img',
      source: 'attribute',
      attribute: 'data-pin-description',
		},
		pin_url: {
			type: 'string',
			default: null,
      selector: 'img',
      source: 'attribute',
      attribute: 'data-pin-url',
		},
		pin_id: {
			type: 'string',
			default: null,
      selector: 'img',
      source: 'attribute',
      attribute: 'data-pin-id',
		},
		pin_nopin: {
			type: 'string',
			default: null,
      selector: 'img',
      source: 'attribute',
      attribute: 'data-pin-nopin',
		},
		pin_media: {
			type: 'string',
			default: null,
      selector: 'img',
      source: 'attribute',
      attribute: 'data-pin-media',
		},
	} );

	return settings;
};

addFilter( 'blocks.registerBlockType', 'meta-for-pinterest/attribute/data-pin', addDataPinAttributes );

/**
 * Creates a panel in the block editor where you can edit the specific blocks that we want this to apply to
 */
const withDataPin = createHigherOrderComponent( ( BlockEdit ) => {
	return ( props ) => {
		// Do nothing if it's another block than our defined ones.
		if ( ! enableDataPinOnBlocks.includes( props.name ) ) {
			return (
				<BlockEdit { ...props } />
			);
		}

		const { pin_nopin, pin_description, pin_url, pin_media, pin_id } = props.attributes;

    // If nopin is set, it won't allow the user to set the other attributes because they don't really matter
    // TODO Should use the MediaUpload button to select the media for the pin as outlined here
    // https://www.liip.ch/en/blog/add-an-image-selector-to-a-gutenberg-block
		return (
			<Fragment>
				<BlockEdit { ...props } />
				<InspectorControls>
					<PanelBody
						title={ __( 'Meta for Pinterest' ) }
						initialOpen={ true }
					>
            <PanelBody>
              <PanelRow>
                <label
                  htmlFor="pinnable-toggle'"
                >
                    { __( 'Pinnable' ) }
                </label>
                <FormToggle
                  checked={ !pin_nopin }
                  onChange={ ( ) => {
                    props.setAttributes( {
                      pin_nopin: pin_nopin ? null : "true" ,
                    } );
                  } }
                />
              </PanelRow>
            </PanelBody>
            { !! pin_nopin ||
              <PanelBody>
                <TextareaControl
                  label={ __( 'Pin Description' ) }
                  value={ pin_description }
                  onChange={ ( description ) => {
                    props.setAttributes( {
                      pin_description: description,
                    } );
                  } }
                />
                <TextControl
                  label={ __( 'Pin URL' ) }
                  value={ pin_url }
                  onChange={ ( url ) => {
                    props.setAttributes( {
                      pin_url: url,
                    } );
                  } }
                />
                <TextControl
                  label={ __( 'Pin ID' ) }
                  value={ pin_id }
                  onChange={ ( id ) => {
                    props.setAttributes( {
                      pin_id: id,
                    } );
                  } }
                />
                <TextControl
                  label={ __( 'Pinned Media' ) }
                  value={ pin_media }
                  onChange={ ( media ) => {
                    props.setAttributes( {
                      pin_media: media,
                    } );
                  } }
                />
                <img src={pin_media} />
              </PanelBody>
            }
					</PanelBody>
				</InspectorControls>
			</Fragment>
		);
	};
}, 'withDataPin' );

addFilter( 'editor.BlockEdit', 'meta-for-pinteres/with-data-pin', withDataPin );

/**
 * Add the actual attributes to the img tag via the props
 * of the img tag
 *
 * @param {object} saveElementProps Props of save element.
 * @param {Object} blockType Block type information.
 * @param {Object} attributes Attributes of block.
 *
 * @returns {object} Modified props of save element.
 */
const addDataPinFields = ( saveElementProps, blockType, attributes ) => {

	// Do nothing if it's another block than our defined ones.
  // There is an extra check here because when we save the
  // element, it pops up again with a slightly different
  // architecture where the children is a direct array
  if (!enableDataPinOnBlocks.includes( blockType.name )) {
    //console.log("Not the right type");
		return saveElementProps;
	}

  // Find the img tag in the children
  // When converting from classic to block, the image might have another
  // nested component
  var img = saveElementProps.children.props;
  try {
    if (Array.isArray(img.children) && img.children[0].type === "img") {
      img = img.children[0];
    } else if (Array.isArray(img.children.props.children) && img.children.props.children[0].type === "img") {
      img = img.children.props.children[0];
    } else {
      return saveElementProps;
    }
  }
  catch (err) {
    console.log("Meta for Pinterest - Failed to find img tag", img);
    // This doesn't match what we expect, exit
    return saveElementProps;
  }

  try {

    // Check each of our attributes to see if they are set. If they aren't set, we shouldn't write the field to the img tag
    if ( attributes.pin_nopin ) {
      assign( img.props, { 'data-pin-nopin' :  attributes.pin_nopin } );
      // If nopin is set, we don't care about the other attributes
      return saveElementProps;
    }

    if ( attributes.pin_description ) {
      assign( img.props, { 'data-pin-description' :  attributes.pin_description} );
    }

    if ( attributes.pin_url ) {
      assign( img.props, { 'data-pin-url' :  attributes.pin_url} );
    }

    if ( attributes.pin_media ) {
      assign( img.props, { 'data-pin-media' :  attributes.pin_media} );
    }

    if ( attributes.pin_id ) {
      assign( img.props, { 'data-pin-id' :  attributes.pin_id} );
    }
  }
  catch(err) {
    // The
    console.log("Meta for Pinterest - Unable to update pinterest meta data", err);
  }

	return saveElementProps;
};

addFilter( 'blocks.getSaveContent.extraProps', 'meta-for-pinterest/get-save-content/extra-props', addDataPinFields );
